import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Copy,
  Check,
  Upload,
  RefreshCw,
  Sparkles,
  Phone,
  FileText,
  User,
  ExternalLink,
  Lock
} from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../common/Toast';
import { formatUzPhone, formatDate } from '../../utils/helpers';

export default function PersonalSubscriptionView({ user, onStatusChange }) {
  const isLawyer = user?.role === 'LAWYER';
  const roleName = isLawyer ? 'Advokat' : 'Mijoz';
  const priceText = isLawyer ? '99 000 so‘m / oy' : '35 000 so‘m / oy';
  const expectedAmount = isLawyer ? 99000 : 35000;

  const [loading, setLoading] = useState(true);
  const [statusData, setStatusData] = useState(null);
  const [pricing, setPricing] = useState({
    cardPan: '5614 6814 2744 1112',
    cardHolder: 'JIZZAX ADVOKATLARI',
  });
  const [copiedCard, setCopiedCard] = useState(false);

  // Receipt submission state
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const [statusRes, priceRes] = await Promise.all([
        api.get('/subscriptions/my-status'),
        api.get('/subscriptions/pricing').catch(() => null),
      ]);

      if (statusRes?.success) {
        setStatusData(statusRes);
        if (onStatusChange) onStatusChange(statusRes);
      }
      if (priceRes?.success) {
        setPricing({
          cardPan: priceRes.cardPan || '5614 6814 2744 1112',
          cardHolder: priceRes.cardHolder || 'JIZZAX ADVOKATLARI',
        });
      }
    } catch (err) {
      console.warn('Failed to load personal subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleCopyCard = () => {
    navigator.clipboard.writeText(pricing.cardPan.replace(/\s+/g, ''));
    setCopiedCard(true);
    showToast(`Karta raqami nusxalandi: ${pricing.cardPan}`, 'success');
    setTimeout(() => setCopiedCard(false), 2000);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Faqat rasm yoki skrinshot fayli qabul qilinadi', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('Fayl hajmi 10MB dan oshmasligi kerak', 'error');
      return;
    }

    // Local preview immediately
    const reader = new FileReader();
    reader.onload = (event) => setReceiptPreview(event.target.result);
    reader.readAsDataURL(file);

    // Upload to server
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('category', 'receipts');

    setUploadingImage(true);
    try {
      const res = await api.postFormData('/upload', uploadData);
      if (res?.success && res?.url) {
        setReceiptFile(res.url);
        showToast('To‘lov cheki muvaffaqiyatli yuklandi!', 'success');
      }
    } catch (err) {
      showToast('Chekni serverga yuklashda xatolik yuz berdi', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmitReceipt = async (e) => {
    e.preventDefault();
    if (!receiptFile && !notes.trim()) {
      showToast('To‘lov cheki rasmini yuklang yoki tranzaksiya raqamini kiriting', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/subscriptions/confirm-payment', {
        receiptUrl: receiptFile,
        notes: notes.trim() || 'To‘lov cheki foydalanuvchi tomonidan yuborildi',
        phone: user?.phone || '',
        paymentMethod: 'CARD_TRANSFER',
      });

      if (res?.success) {
        showToast(
          res.message || 'To‘lov chekingiz qabul qilindi va ma’muriyatga tekshirish uchun yuborildi!',
          'success'
        );
        setReceiptFile(null);
        setReceiptPreview(null);
        setNotes('');
        fetchStatus();
      } else {
        showToast(res?.message || 'Yuborishda xatolik', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Yuborishda xatolik yuz berdi', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const isFree = statusData?.isFreeExempt || statusData?.status === 'FREE_PERMANENT';
  const isActive = statusData?.isActive && !isFree;
  const isPending = statusData?.status === 'PENDING';
  const isExpired = !statusData?.isActive && !isFree && !isPending;

  return (
    <div className="space-y-6 max-w-5xl mx-auto animate-admin-fade-in">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-[#091322] via-[#0d1d36] to-[#091322] rounded-xl p-5 sm:p-6 border border-[#1b2b46] shadow-xl text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3.5">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#c5a880]/20 to-[#c5a880]/10 border border-[#c5a880]/50 flex items-center justify-center text-[#c5a880] shadow-md shrink-0">
            <CreditCard className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg sm:text-xl font-bold tracking-tight text-white">
                Mening Obuna Holatim
              </h1>
              <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-[#c5a880]/20 text-[#c5a880] border border-[#c5a880]/40">
                {roleName} (30 kunlik)
              </span>
            </div>
            <p className="text-xs text-slate-300 mt-1">
              Platforma xizmatlaridan foydalanish muddati, rekvizitlar va to‘lov chekini tasdiqlash
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={fetchStatus}
          disabled={loading}
          className="px-3.5 py-2 rounded-lg bg-[#142238] hover:bg-[#1b2d49] border border-slate-700 text-xs font-semibold text-slate-200 flex items-center cursor-pointer transition-all active:scale-95 disabled:opacity-50 shrink-0"
          title="Holatni yangilash"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Yangilash</span>
        </button>
      </div>

      {/* Account Info & Status Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* User Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-2xs admin-card-interactive flex flex-col justify-between">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 font-bold shrink-0">
              <User className="w-5 h-5 text-slate-600" />
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase font-bold text-slate-400">Foydalanuvchi</div>
              <div className="text-sm font-bold text-slate-900 truncate">{user?.fullName || 'Foydalanuvchi'}</div>
              <div className="text-[11px] text-slate-500 font-mono">{user?.phone ? formatUzPhone(user.phone) : '—'}</div>
            </div>
          </div>
          <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
            <span>Rol:</span>
            <span className="font-semibold text-slate-800">{roleName}</span>
          </div>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-2xs admin-card-interactive flex flex-col justify-between md:col-span-2">
          <div className="flex items-start justify-between">
            <div>
              <span className="text-xs uppercase font-bold text-slate-400 block mb-1">Joriy Holat</span>
              {isFree ? (
                <div className="flex items-center space-x-2">
                  <div className="p-1 rounded-full bg-purple-100 text-purple-700">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-purple-900">
                      Cheksiz Bepul Ruxsat Faol
                    </h3>
                    <p className="text-xs text-purple-700 mt-0.5">
                      Ma’muriyat tomonidan sizga tizimdan to‘liq bepul foydalanish huquqi taqdim etilgan.
                    </p>
                  </div>
                </div>
              ) : isActive ? (
                <div className="flex items-center space-x-2">
                  <div className="p-1 rounded-full bg-emerald-100 text-emerald-700">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-emerald-900">
                      Obunangiz Faol ({statusData?.daysLeft || 0} kun qoldi)
                    </h3>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      Tugash sanasi: {statusData?.subscription?.endDate ? formatDate(statusData.subscription.endDate) : '30 kun'}
                    </p>
                  </div>
                </div>
              ) : isPending ? (
                <div className="flex items-center space-x-2">
                  <div className="p-1 rounded-full bg-amber-100 text-amber-700">
                    <Clock className="w-5 h-5 animate-spin" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-amber-900">
                      To‘lov Cheki Tekshirilmoqda
                    </h3>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Siz yuborgan to‘lov cheki admin tomonidan ko‘rib chiqilmoqda. Tez orada tasdiqlanadi.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <div className="p-1 rounded-full bg-rose-100 text-rose-700">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-rose-900">
                      Ruxsat To‘xtatilgan / Obuna Muddati Tugagan
                    </h3>
                    <p className="text-xs text-rose-700 mt-0.5">
                      Tizimdan to‘liq foydalanish uchun oylik to‘lovni amalga oshiring va chekni yuklang.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="text-right shrink-0">
              <span className="text-[10px] uppercase font-bold text-slate-400 block">Xizmat narxi</span>
              <span className="text-sm sm:text-base font-mono font-bold text-[#c5a880]">
                {priceText}
              </span>
            </div>
          </div>

          <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span>Tizim xavfsizlik protokoli:</span>
            <span className="font-semibold text-emerald-600 flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>256-bit SSL Himoyalangan</span>
            </span>
          </div>
        </div>
      </div>

      {/* Official Payment Card Info Box */}
      <div className="bg-gradient-to-br from-[#0c1626] to-[#08101c] rounded-2xl p-5 sm:p-6 border border-[#1b2b46] shadow-xl text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#c5a880]/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 relative z-10">
          <div>
            <span className="text-[11px] uppercase font-bold tracking-widest text-[#c5a880] flex items-center gap-1.5 mb-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Rasmiy To‘lov Qabul Qilish Kartasi (Click & Payme)</span>
            </span>
            <div className="flex items-center space-x-3 mt-1 flex-wrap">
              <span className="font-mono text-2xl sm:text-3xl font-extrabold tracking-wider text-white">
                {pricing.cardPan}
              </span>
              <button
                type="button"
                onClick={handleCopyCard}
                className="px-3 py-1.5 rounded-lg text-xs font-bold bg-[#17253d] hover:bg-[#223554] text-[#c5a880] border border-[#c5a880]/40 flex items-center transition-all active:scale-95 cursor-pointer shadow-xs"
                title="Karta raqamini nusxalash"
              >
                {copiedCard ? (
                  <>
                    <Check className="w-3.5 h-3.5 mr-1 text-emerald-400" />
                    <span>Nusxalandi!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5 mr-1" />
                    <span>Nusxalash</span>
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-slate-300 mt-2">
              Karta egasi: <strong className="text-white font-semibold">{pricing.cardHolder}</strong> | 
              Oylik xizmat to‘lovi: <strong className="text-[#c5a880] font-mono">{priceText}</strong>
            </p>
          </div>

          <div className="flex flex-row md:flex-col gap-2 shrink-0">
            <a
              href="https://payme.uz"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg bg-[#00cccc]/20 hover:bg-[#00cccc]/30 border border-[#00cccc]/50 text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow-xs"
            >
              <span>Payme orqali to‘lash</span>
              <ExternalLink className="w-3 h-3 text-[#00cccc]" />
            </a>
            <a
              href="https://click.uz"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg bg-[#0088cc]/20 hover:bg-[#0088cc]/30 border border-[#0088cc]/50 text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow-xs"
            >
              <span>Click orqali to‘lash</span>
              <ExternalLink className="w-3 h-3 text-[#0088cc]" />
            </a>
          </div>
        </div>
      </div>

      {/* Upload Payment Receipt Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 shadow-2xs">
        <div className="flex items-center space-x-2.5 mb-4 pb-3 border-b border-slate-100">
          <Upload className="w-5 h-5 text-[#c5a880]" />
          <div>
            <h2 className="text-sm sm:text-base font-bold text-slate-900">
              To‘lov Chekini Tasdiqlash Uchun Yuborish
            </h2>
            <p className="text-xs text-slate-500">
              Kartaga to‘lov qilgach, to‘lov skrinshotini yuklang yoki tranzaksiya raqamini qoldiring. Admin tekshirib darhol hisobingizni 30 kunga faollashtiradi.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmitReceipt} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* File Upload Area */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                To‘lov Cheki (Skrinshot yoki Fotosurat) *
              </label>
              <div className="border-2 border-dashed border-slate-300 hover:border-[#c5a880] rounded-xl p-4 text-center bg-slate-50 transition-colors cursor-pointer relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={uploadingImage}
                />
                {receiptPreview ? (
                  <div className="space-y-2">
                    <img
                      src={receiptPreview}
                      alt="Chek rasmi"
                      className="max-h-40 mx-auto rounded-lg shadow-sm object-contain border"
                    />
                    <div className="text-[11px] text-emerald-600 font-semibold flex items-center justify-center gap-1">
                      <Check className="w-3.5 h-3.5" />
                      <span>Chek tanlandi ({uploadingImage ? 'Yuklanmoqda...' : 'Yuklandi'})</span>
                    </div>
                  </div>
                ) : (
                  <div className="py-4">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-slate-700">
                      Chek rasmini bu yerga tashlang yoki tanlang
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      JPG, PNG, WEBP formatlar (maksimal 10MB)
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes / Details Area */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  Izoh yoki Tranzaksiya Ma’lumotlari (Ixtiyoriy)
                </label>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Masalan: Click orqali 99 000 so‘m o‘tkazdim, tranzaksiya №: 981248"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:border-[#c5a880] focus:bg-white resize-none"
                />
              </div>

              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-600 space-y-1">
                <div className="font-semibold text-slate-800">Qo‘shimcha ma’lumot:</div>
                <p>• To‘lov qabul qilingach 5-15 daqiqa ichida admin tomonidan tasdiqlanadi.</p>
                <p>• Tezkor tasdiqlash uchun ma’muriyat bilan bog‘lanishingiz mumkin: <strong>+998 (91) 596-77-55</strong></p>
              </div>
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              disabled={submitting || uploadingImage || (!receiptFile && !notes.trim())}
              className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-bold text-xs uppercase tracking-wider shadow-md hover:shadow-lg transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Yuborilmoqda...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>To‘lov Chekini Tasdiqlash Uchun Yuborish</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
